import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

// Generic device viewport sizes in CSS pixels (portrait orientation)
const DEVICE_PRESETS = {
  phone: { width: 390, height: 844 }, // Generic modern phone (e.g., iPhone 14 logical px)
  tablet: { width: 768, height: 1024 }, // Generic tablet (iPad portrait)
  laptop: { width: 900, height: 600 }, // Generic 13" laptop (fits better in edit panel)
  desktop: { width: 1000, height: 700 }, // Generic desktop viewport (reasonable for edit view)
};

export const PreviewContext = createContext(null);

export const usePreview = () => {
  const ctx = useContext(PreviewContext);
  if (!ctx) throw new Error("usePreview must be used within a PreviewProvider");
  return ctx;
};

export const usePreviewOptional = () => {
  return useContext(PreviewContext);
};

export const PreviewProvider = ({ children, valueOverrides }) => {
  const [mode, setModeState] = useState("responsive"); // responsive|phone|tablet|laptop|desktop
  const [orientation, setOrientation] = useState("portrait"); // portrait|landscape
  const [showFrame, setShowFrame] = useState(true);
  const [manualZoom, setManualZoom] = useState(0.8); // Manual zoom scale (0.2 to 2.0)
  const [containerWidth, setContainerWidthState] = useState(1200); // The active container width for rendering

  // Reset orientation whenever switching preview modes.
  // Example: phone (landscape) -> tablet should default back to portrait.
  const setMode = useCallback((nextMode) => {
    setModeState((prev) => {
      const resolved = typeof nextMode === "function" ? nextMode(prev) : nextMode;
      if (resolved !== prev) {
        setOrientation("portrait");
      }
      return resolved;
    });
  }, []);

  // Wrap setContainerWidth in useCallback to maintain stable reference
  const setContainerWidth = useCallback((width) => {
    setContainerWidthState((prev) => {
      // Normalize to a finite positive integer width
      const next =
        typeof width === "number"
          ? Math.round(width)
          : typeof width === "string"
            ? Math.round(parseFloat(width))
            : Number.NaN;

      if (!Number.isFinite(next) || next <= 0) return prev;

      // Ignore tiny measurement jitter (common with animated panels / subpixel layout)
      // to prevent feedback loops like ResizeObserver -> setState -> layout -> ResizeObserver...
      if (Number.isFinite(prev) && Math.abs(prev - next) < 2) return prev;

      return next;
    });
  }, []);

  const getPresetSize = useCallback((preset) => {
    const base = DEVICE_PRESETS[preset];
    if (!base) return null;

    // Orientation should only affect phone/tablet.
    // Desktop/laptop are conceptually landscape already and should not "rotate" when phone/tablet
    // orientation is toggled in the UI.
    const orientationApplies = preset === "phone" || preset === "tablet";
    if (!orientationApplies || orientation === "portrait") return base;

    return { width: base.height, height: base.width };
  }, [orientation]);

  const viewport = useMemo(() => {
    if (mode === "responsive") return null;
    return getPresetSize(mode) || null;
  }, [mode, getPresetSize]);

  const frameKind = useMemo(() => {
    switch (mode) {
      case "phone":
        return "phone";
      case "tablet":
        return "tablet";
      case "laptop":
        return "laptop";
      case "desktop":
        return "desktop";
      default:
        return null;
    }
  }, [mode]);

  // Memoize the context value to prevent unnecessary re-renders
  // Note: most setState functions are stable, but setMode is a wrapped callback so it IS in deps.
  const baseValue = useMemo(() => ({
    mode,
    setMode,
    orientation,
    setOrientation,
    showFrame,
    setShowFrame,
    manualZoom,
    setManualZoom,
    viewport, // { width, height } | null when responsive
    frameKind, // phone|tablet|laptop|desktop|null
    presets: DEVICE_PRESETS,
    containerWidth, // The active container width for rendering
    setContainerWidth, // Method to update container width (stable reference)
    // Keep old names for backward compatibility during migration
    contentWidth: containerWidth,
    setContentWidth: setContainerWidth,
  }), [mode, setMode, orientation, showFrame, manualZoom, viewport, frameKind, containerWidth, setContainerWidth]);

  const value = useMemo(() => {
    if (!valueOverrides) return baseValue;
    return { ...baseValue, ...valueOverrides };
  }, [baseValue, valueOverrides]);

  return (
    <PreviewContext.Provider value={value}>{children}</PreviewContext.Provider>
  );
};


export default PreviewContext;


